import {Plural, Trans} from '@lingui/macro';
import _, {isUndefined} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {BoxedIcon} from '../../components/common/boxedIcon';
import {searchAnswersAndFolders} from '../../components/inboxes/composer/answers/answersSearchHelpers';
import {makeKeyForAnswerOrFolder} from '../../helpers/models/answerHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {mergeAsyncStatuses} from '../../helpers/resourceTableStatusHelpers';
import {AnswerFolderModel, AnswerOrFolderModel, RAnswerFolder} from '../../models/api/answerFolderModel';
import {AnswerModel} from '../../models/api/answerModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {AppState} from '../../store/appState';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {
  getAnswerFoldersAsyncStatus,
  makeGetAnswerFoldersInNamespaces,
} from '../../store/data/answerFolders/answerFolderSelectors';
import {fetchAnswersAndFoldersAction} from '../../store/data/answers/answerActions';
import {moveAnswersOrFolderBulkAsync} from '../../store/data/answers/answerBulkActions';
import {getAnswersAsyncStatus} from '../../store/data/answers/answersSelectors';
import {SettingsResourceDialogListItem} from '../common/dialog/settingsResourceDialogListItem';
import {SettingsResourceListDialog} from '../common/dialog/settingsResourceListDialog';
import {getSettingsType} from '../rules/ruleHelpers';
import {MoveAnswerDialogToplineContainer} from './moveAnswerDialogToplineContainer';

/*
 * Props.
 */

interface MoveAnswerDialogContainerProps {
  itemsToActOn: ReadonlyArray<AnswerOrFolderModel>;
  settingsAnswersUrl: SettingsAnswersUrl;
  onRequestClose: Handler;
  onMove: Handler;
  onAddFolderClick: HandlerOf<number | undefined>;
}

const noFolderId = Symbol('noFolderId');
type NoFolderId = typeof noFolderId;
type SomeFolderId = number;
type ParentFolderId = SomeFolderId | NoFolderId;

/*
 * Style.
 */

interface SelectedStyleProps {
  $isSelected?: boolean;
}

const StyledCircleSeparatorSpan = styled.span<SelectedStyleProps>`
  display: inline-block;
  border-radius: 100px;
  background-color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade70)};
  margin: 1px 2px 0px 2px;
  width: 10px;
  height: 10px;
  transform: scale(0.49); // Without this, the "circle" becomes more of a rounded rectangle.
`;

/*
 * Component.
 */

const MoveAnswerDialogContainerComponent: FC<
  MoveAnswerDialogContainerProps &
    ResolveStateProps<typeof mapStateToProps> &
    ResolveDispatchProps<typeof mapDispatchToProps>
> = (props) => {
  const theme = useUserTheme();
  const {settingsAnswersUrl, itemsToActOn} = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [listParentFolderId, setListParentFolderId] = useState(
    (settingsAnswersUrl.inFolder && settingsAnswersUrl.folderId) || undefined,
  );

  const [newFolderId, setNewFolderId] = useState<ParentFolderId | undefined>(undefined);

  const listItems = useMemo(() => {
    if (!searchQuery) {
      return props.folders.filter((item) => item.parentFolderId === listParentFolderId);
    }

    return searchAnswersAndFolders(props.folders, searchQuery);
  }, [listParentFolderId, props.folders, searchQuery]);
  const itemsIdMap = useMemo(
    () =>
      _(listItems)
        .uniqBy((i) => makeKeyForAnswerOrFolder(i))
        .keyBy((i) => makeKeyForAnswerOrFolder(i))
        .value(),
    [listItems],
  );

  const itemIdsToActOn = _(itemsToActOn)
    .map((i) => i.id)
    .value();
  const itemIds = useMemo(
    () => listItems.filter((i) => !itemIdsToActOn.includes(i.id)).map((i) => makeKeyForAnswerOrFolder(i)),
    [listItems, itemIdsToActOn],
  );

  const onOkMoveClick = async () => {
    if (!newFolderId) {
      return;
    }

    const folderId = newFolderId === noFolderId ? undefined : newFolderId;

    const [foldersToActOn, answersToActOn] = _(itemsToActOn)
      .partition((i) => RAnswerFolder.is(i))
      .value() as [AnswerFolderModel[], AnswerModel[]];

    await props.moveAnswersOrFolderBulkAsync(
      settingsAnswersUrl.namespace,
      folderId,
      foldersToActOn.map((i) => i.id),
      answersToActOn.map((i) => i.id),
    );
    props.onMove();
    answersToActOn.forEach((answer) => {
      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_MOVE, {
        id: answer.id,
        to_canned_answer_folder_id: folderId,
        canned_answer_folder_id: answer.folderId,
        if_nested_folder: Boolean(answer),
        settings_type: getSettingsType(settingsAnswersUrl.namespace),
      });
    });
    if (foldersToActOn.length > 0) {
      trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_FOLDER_MOVE, {
        canned_answer_folder_ids: foldersToActOn.map((i) => i.id),
        to_canned_answer_folder_id: folderId,
        has_sub_folders: foldersToActOn.some((f) => f.numChildFolders > 0),
        has_canned_responses: foldersToActOn.some((f) => f.numChildAnswers > 0),
        settings_type: getSettingsType(settingsAnswersUrl.namespace),
      });
    }
  };

  const onFolderClick = (item: AnswerFolderModel) => {
    const hasChildren = item.numChildAnswers + item.numChildFolders > 0;
    setNewFolderId(item.id);

    if (hasChildren) {
      setListParentFolderId(item.id);
    }
  };

  function renderIcon(item: AnswerFolderModel) {
    const hasChildren = item.numChildAnswers + item.numChildFolders > 0;
    const isSelected = newFolderId === item.id;

    if (isSelected) {
      return <Icon name="checkmarkSelectedSmall" color={theme.palette.blue.shade40} />;
    }

    if (hasChildren) {
      return <Icon name="chevronRightSmall" color={theme.greys.shade60} />;
    }

    return null;
  }

  function renderItem(item: AnswerFolderModel) {
    const isSelected = newFolderId === item.id;
    const hasChildren = item.numChildAnswers + item.numChildFolders > 0;

    return (
      <SettingsResourceDialogListItem
        iconPart={
          <BoxedIcon
            name="folderSmall"
            backgroundColor={theme.greys.shade30}
            color={theme.greys.shade70}
            size={VisualSizesEnum.SMALL}
          />
        }
        isSelected={isSelected}
        onClick={() => onFolderClick(item)}
        title={item.name}
        rightIconPart={renderIcon(item)}
        subtitle={
          hasChildren && (
            <>
              <Plural
                id="settingstable-subfolders"
                value={item.numChildFolders || 0}
                one="# folder"
                other="# folders"
              />
              <StyledCircleSeparatorSpan $isSelected={isSelected} />
              <Plural
                id="settingstable-subanswers"
                value={item.numChildAnswers || 0}
                one="# template"
                other="# templates"
              />
            </>
          )
        }
      />
    );
  }

  function renderNone() {
    const isSelected = newFolderId === noFolderId;

    return (
      <SettingsResourceDialogListItem
        onClick={() => setNewFolderId(noFolderId)}
        iconPart={
          <BoxedIcon
            name="noneSmall"
            backgroundColor={theme.greys.shade30}
            color={theme.greys.shade70}
            size={VisualSizesEnum.SMALL}
          />
        }
        isSelected={isSelected}
        rightIconPart={
          isSelected && <Icon name="checkmarkSelectedSmall" color={theme.palette.blue.shade40} />
        }
        title={<Trans id="settingstable-none">None</Trans>}
      />
    );
  }

  const moreStatuses = useMemo(
    () => ({
      [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
      [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
    }),
    [],
  );

  const itemIdsWithNone: ReadonlyArray<string | NoFolderId> = isUndefined(listParentFolderId)
    ? [noFolderId, ...itemIds]
    : itemIds;

  return (
    <SettingsResourceListDialog
      moreStatuses={moreStatuses}
      title={<Plural value={itemsToActOn.length} one="Move 1 item under..." other="Move # items under..." />}
      searchQuery={searchQuery}
      onChange={setSearchQuery}
      topline={
        <MoveAnswerDialogToplineContainer
          onFolderClick={(folder) => {
            setListParentFolderId(folder?.id);
            setNewFolderId(folder?.id);
          }}
          folderId={listParentFolderId}
        />
      }
      headerActionLabel={<Trans id="settingsfoldermove-addfolder">Add folder</Trans>}
      onHeaderActionClick={() => props.onAddFolderClick(listParentFolderId)}
      onConfirmClick={onOkMoveClick}
      confirmLabel={<Trans id="settingsfoldermove-move">Move</Trans>}
      itemIds={itemIdsWithNone}
      onRequestClose={props.onRequestClose}
      emptyLabel={<Trans id="settingsfoldermove-nofolders">No folders</Trans>}
      renderItem={(id) => {
        if (id === noFolderId) {
          return renderNone();
        }

        const item = itemsIdMap[id];

        if (!item) {
          return null;
        }

        return renderItem(item);
      }}
    />
  );
};

/*
 * Selectors.
 */

function getNamespaces(_state: AppState, props: MoveAnswerDialogContainerProps) {
  return [props.settingsAnswersUrl.namespace];
}

const getAsyncStatus = createSelector(
  [getAnswersAsyncStatus, getAnswerFoldersAsyncStatus],
  (answersAsyncStatus, answerFoldersAsyncStatus) =>
    mergeAsyncStatuses([answersAsyncStatus, answerFoldersAsyncStatus]),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MoveAnswerDialogContainerProps>()(() => {
  const getAnswerFoldersInNamespaces = makeGetAnswerFoldersInNamespaces(getNamespaces);

  return {
    loadingStatus: getAsyncStatus,
    folders: getAnswerFoldersInNamespaces,
  };
});
const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchAllAnswersAndFolders: fetchAnswersAndFoldersAction,
  moveAnswersOrFolderBulkAsync,
}));

export const MoveAnswerDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MoveAnswerDialogContainerComponent);
